import React, { useMemo, useState } from "react";
import Select from "react-select";
import { useCollection } from "../../hooks/useCollection";
import "../gestioneDocumenti/GestioneDocumenti.css";

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getMonthKey = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const formatDate = (date) => {
  if (!date) return "—";
  return date.toLocaleDateString();
};

const numToMoney = (value) => {
  if (value == null || value === "") return "—";
  const n =
    typeof value === "number"
      ? value
      : Number(String(value).replace(",", "."));
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
};

const getDocumentoRows = (docs) => {
  const safeDocs = Array.isArray(docs) ? docs : [];

  return safeDocs.map((doc) => ({
    id: doc?.id || `${Math.random()}`,
    codiceSettore:
      doc?.codiceArticolo || doc?.codiceMateriale ? (doc?.codice || "") : "",
    codice:
      doc?.codiceArticolo ||
      doc?.codiceMateriale ||
      doc?.codice ||
      doc?.sku ||
      "",
    descrizione: doc?.descrizione || doc?.nome || "",
    unitaMisura:
      doc?.unita ||
      doc?.unita_misura ||
      doc?.unitaMisura ||
      doc?.um ||
      doc?.unit ||
      "",
    prezzo: doc?.prezzo ?? doc?.prezzoUnitario ?? doc?.costo ?? "",
    dataAcquisto: toJsDate(doc?.dataBolla || doc?.dataAcquisto || doc?.data),
    fornitore: doc?.fornitore || doc?.supplier || doc?.azienda || "",
    codiceBolla: doc?.codiceBolla || doc?.numero_documento || "",
    linkBolla: doc?.linkBolla || doc?.fotoUrl || "",
    raw: { doc },
  }));
};

export default function RegistroAcquisti() {
  const { documents, error } = useCollection("registroAcquisti");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterFornitori, setFilterFornitori] = useState([]);
  const [filterSettoreCodicePrefix, setFilterSettoreCodicePrefix] =
    useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const allRows = useMemo(() => getDocumentoRows(documents), [documents]);

  const fornitoreOptions = useMemo(() => {
    const unique = new Set();
    allRows.forEach((r) => {
      const f = (r?.fornitore ?? "").toString().trim();
      if (f) unique.add(f);
    });
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b, "it"))
      .map((f) => ({ value: f, label: f }));
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedFornitori = new Set(
      (filterFornitori || []).map((o) => (o?.value ?? "").toString()),
    );

    return allRows
      .filter((r) => {
        if (!filterSettoreCodicePrefix) return true;
        const c = (r?.codiceSettore ?? "").toString().trim();
        return c.startsWith(filterSettoreCodicePrefix);
      })
      .filter((r) => {
        if (!filterMonth) return true;
        const date = toJsDate(r?.dataAcquisto);
        return getMonthKey(date) === filterMonth;
      })
      .filter((r) => {
        if (selectedFornitori.size === 0) return true;
        const f = (r?.fornitore ?? "").toString();
        return selectedFornitori.has(f);
      })
      .filter((r) => {
        if (!term) return true;
        const codice = (r?.codice ?? "").toString().toLowerCase();
        const descrizione = (r?.descrizione ?? "").toString().toLowerCase();
        const fornitore = (r?.fornitore ?? "").toString().toLowerCase();
        const codiceBolla = (r?.codiceBolla ?? "").toString().toLowerCase();
        const um = (r?.unitaMisura ?? "").toString().toLowerCase();
        return (
          codice.includes(term) ||
          descrizione.includes(term) ||
          fornitore.includes(term) ||
          codiceBolla.includes(term) ||
          um.includes(term)
        );
      });
  }, [
    allRows,
    filterFornitori,
    filterMonth,
    filterSettoreCodicePrefix,
    searchTerm,
  ]);

  return (
    <div className="gestione-documenti">
      <div className="gestione-header">
        <div>
          <h1>Registro Acquisti</h1>
          <p>
            {filteredRows.length} righe (su {allRows.length} totali)
          </p>
        </div>
      </div>

      <div className="gd-filters">
        <div className="gd-filter-row">
          <div className="gd-filter-field">
            <label>Settore</label>
            <div className="gd-chips">
              <button
                type="button"
                className={`gd-chip ${
                  filterSettoreCodicePrefix === "" ? "active" : ""
                }`}
                onClick={() => setFilterSettoreCodicePrefix("")}
              >
                Tutti
              </button>
              <button
                type="button"
                className={`gd-chip ${
                  filterSettoreCodicePrefix === "1" ? "active" : ""
                }`}
                onClick={() => setFilterSettoreCodicePrefix("1")}
              >
                Elettrico
              </button>
              <button
                type="button"
                className={`gd-chip ${
                  filterSettoreCodicePrefix === "2" ? "active" : ""
                }`}
                onClick={() => setFilterSettoreCodicePrefix("2")}
              >
                Edile
              </button>
              <button
                type="button"
                className={`gd-chip ${
                  filterSettoreCodicePrefix === "5" ? "active" : ""
                }`}
                onClick={() => setFilterSettoreCodicePrefix("5")}
              >
                Cucine
              </button>
            </div>
          </div>

          <div className="gd-filter-field gd-search">
            <label>Cerca</label>
            <input
              type="text"
              className="search-input"
              placeholder="Codice, descrizione, fornitore..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="gd-filter-field">
            <label>Periodo</label>
            <div className="gd-month">
              <input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
              />
              <button
                type="button"
                className="gd-clear-btn"
                onClick={() => setFilterMonth("")}
                disabled={!filterMonth}
                aria-label="Rimuovi filtro periodo"
                title="Rimuovi filtro periodo"
              >
                ×
              </button>
            </div>
          </div>

          <div className="gd-filter-field gd-clienti">
            <label>Fornitori</label>
            <Select
              classNamePrefix="react-select"
              value={filterFornitori}
              onChange={(opts) => setFilterFornitori(opts || [])}
              options={fornitoreOptions}
              isMulti
              isSearchable
              isClearable
              placeholder="Filtra fornitori..."
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {!error && filteredRows.length === 0 && (
        <p className="no-docs">Nessun dato disponibile con i filtri attuali.</p>
      )}

      {filteredRows.length > 0 && (
        <div className="project-table-wrapper">
          <table className="project-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Descrizione</th>
                <th>UM</th>
                <th>Prezzo</th>
                <th>Data acquisto</th>
                <th>Fornitore</th>
                <th>Codice bolla</th>
                <th>Documento</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.codice || "—"}</td>
                  <td>{row.descrizione || "—"}</td>
                  <td>{row.unitaMisura || "—"}</td>
                  <td>{numToMoney(row.prezzo)}</td>
                  <td>{formatDate(toJsDate(row.dataAcquisto))}</td>
                  <td>{row.fornitore || "—"}</td>
                  <td>{row.codiceBolla || "—"}</td>
                  <td>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn-icon download"
                        onClick={() => {
                          const url = row.linkBolla || "";
                          if (!url) return;
                          setPreviewUrl(url);
                          setPreviewTitle(
                            row.codiceBolla
                              ? `Bolla ${row.codiceBolla}`
                              : "Documento",
                          );
                        }}
                        title={row.linkBolla ? "Apri anteprima" : "Nessun file"}
                        disabled={!row.linkBolla}
                      >
                        <span className="material-icons">visibility</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewUrl && (
        <div
          className="modal-overlay"
          onClick={() => {
            setPreviewUrl("");
            setPreviewTitle("");
          }}
        >
          <div
            className="modal-content-documenti"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "95vw",
              maxWidth: "1100px",
              height: "85vh",
              minWidth: "320px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <button
              className="close-modal"
              onClick={() => {
                setPreviewUrl("");
                setPreviewTitle("");
              }}
              aria-label="Chiudi anteprima"
              title="Chiudi"
            >
              &times;
            </button>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid #eee" }}>
              <h2 style={{ margin: 0, fontSize: "1.1rem" }}>
                {previewTitle || "Documento"}
              </h2>
            </div>
            <div style={{ flex: 1, background: "#333" }}>
              <embed
                src={previewUrl}
                width="100%"
                height="100%"
                type="application/pdf"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
